import React, { useState } from "react";
import { useQuery } from "react-query";
import { getAutoNotificationCreatedEvent, getChoicesList, updateSurveyEventStatus } from "../../../../services/choices/choices_services";

import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Button,
  Switch,
} from "@mui/material";

import {
  Visibility,
  Edit,
  ToggleOn,
  Assessment,
  Download,
} from "@mui/icons-material";

import QuestionEditAndViewModal from "./survey_questions_edit_view_modal";
import { exportAllSurveyNotificationLogs } from "../../../../services/choices/choices_services";
import { useNavigate, useSearchParams } from "react-router-dom";
import { queryClient } from "../../../../providers/query/query";
import useUser from "../../../../providers/user/user_provider";
import SurveySelectModal from "./auto_notification_survey_modal";

const AutoNotificationCreatedEvent = () => {
  const navigate = useNavigate();
  const { hasPermission, userPermissions } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = React.useState(searchParams.get("page") ? Number(searchParams.get("page")) : 1);

  const [openModal, setOpenModal] = React.useState(false);
  const [open, setOpen] = useState(false);
  const [download, setDownload] = useState(false);
  const [downloadLoading, setDownloadLoading] =
      React.useState(false);



  const [modalMode, setModalMode] = React.useState<
    "view" | "edit"
  >("view");

  const [selectedItem, setSelectedItem] =
    React.useState<any>(null);
  const [statusLoadingId, setStatusLoadingId] =
    React.useState<number | null>(null);

  const { data: choicesListData } = useQuery(
    ["choicesListing",],
    async () => {
      let response = await getChoicesList({
        keyword: '',
        // admin-choices-service's v2 list endpoint caps size at 100 - a
        // higher value is rejected outright with a 400, which (combined
        // with this query's retry: false) silently left this dropdown
        // empty with no visible error.
        page: 1, size: 100,
        dateFrom: null,
        dateTo: null,
        validFrom: null,
        validTo: null,
        sortOrder: 'DESC',
      });
      return response?.data?.data || [];
    },
    {
      retry: false
    }
  );

  const handleDownloadEvents = async () => {
      try {
        const response = await exportAllSurveyNotificationLogs();
  
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
  
        const url = window.URL.createObjectURL(blob);
  
        const a = document.createElement("a");
        a.href = url;
        a.download = "notification-events-file.xlsx";
  
        document.body.appendChild(a);
        a.click();
  
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed", error);
      }
    };

  const { data, isLoading, isFetching, isError } = useQuery(
    ["autoNotificationCreatedEvent", page],
    async () => {
      const response =
        await getAutoNotificationCreatedEvent(
          page,
          10
        );

      return response?.data?.data || {};
    },
    {
      keepPreviousData: true,
    }
  );

  const list = data?.list || [];
  const totalCount = data?.total || 0;

  const handleChangePage = (
  event: React.MouseEvent<HTMLButtonElement> | null,
  newPage: number
) => {
  const updatedPage = newPage + 1;

  setPage(updatedPage);

  setSearchParams((prev) => {
    prev.set("page", updatedPage.toString());
    return prev;
  });
};

  return (
    <Box p={3}>
      {/* HEADER */}
      <Box mb={3} display={'flex'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={2}>
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
          >
            Auto Notification Events
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            mt={0.5}
          >
            View and manage auto notification survey
            events
          </Typography>
        </Box>

        {hasPermission('choices', 'create') &&
          <Box display="flex" justifyContent="space-between" gap={3} mt={2}>
            <Button
            sx={{width:'100%'}}
              disabled={!hasPermission("choices", "create")}
              variant="contained"
              onClick={() => {
                setOpen(true);
              }}
              fullWidth
              color={"primary"}
            >
              + Create Auto Notification
            </Button>
            <Button
              sx={{width:'100%'}}
              variant="contained"
              startIcon={
                !downloadLoading ? <Download /> : undefined
              }
              disabled={downloadLoading}
              onClick={async () => {
                try {
                  setDownloadLoading(true);
                  await handleDownloadEvents();
                } finally {
                  setDownloadLoading(false);
                }
              }}
            >
              {downloadLoading ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                />
              ) : (
                "Download All Logs"
              )}
            </Button>
          </Box>
        }
      </Box>

      {/* LOADING */}
      {isLoading || isFetching ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh"
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">
          Failed to load data. Please try again.
        </Typography>
      ) : (
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ maxHeight: 650 }}>
            <Table stickyHeader>
              {/* HEADER */}
              <TableHead>
                <TableRow>
                  {[
                    'S.No.',
                    "ID",
                    "Survey",
                    "Question",
                    "Answer",
                    "Created By",
                    "Created At",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      align={
                        head === "Actions"
                          ? "center"
                          : "left"
                      }
                      sx={{
                        fontWeight: 700,
                        backgroundColor: "#f5f5f5",
                        whiteSpace: "nowrap",
                        minWidth:
                          head === "Question"
                            ? 300
                            : head === "Actions"
                              ? 240
                              : 150,
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>
                {list.length > 0 ? (
                  list.map(
                    (
                      item: any,
                      index: number
                    ) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? "#fff"
                              : "#fafafa",

                          transition:
                            "0.2s ease-in-out",

                          "&:hover": {
                            backgroundColor:
                              "#f4f8ff",
                          },
                        }}
                      >
                        <TableCell>
                          {index+(page-1)*10 + 1}.
                        </TableCell>
                        {/* ID */}
                        <TableCell>
                          {item.id}
                        </TableCell>

                        {/* SURVEY */}
                        <TableCell>
                          <Typography
                            fontWeight={600}
                          >
                            {item.choiceName}
                          </Typography>
                        </TableCell>

                        {/* QUESTION */}
                        <TableCell
                          sx={{
                            maxWidth: 320,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow:
                                "ellipsis",
                              display:
                                "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient:
                                "vertical",
                            }}
                          >
                            {
                              item.question_title
                            }
                          </Typography>
                        </TableCell>

                        {/* ANSWER */}
                        <TableCell>
                          <Chip
                            label={item.answer}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              maxWidth: 200,
                            }}
                          />
                        </TableCell>

                        {/* CREATED BY */}
                        <TableCell>
                          {item.createdBy}
                        </TableCell>

                        {/* CREATED AT */}
                        <TableCell>
                          <Typography
                            variant="body2"
                          >
                            {new Date(
                              item.createdAt
                            ).toLocaleDateString()}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {new Date(
                              item.createdAt
                            ).toLocaleTimeString()}
                          </Typography>
                        </TableCell>

                        {/* ACTIONS */}
                        <TableCell align="center">
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                          >
                            {/* VIEW */}
                            <Tooltip title="View">
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  setSelectedItem(
                                    item
                                  );

                                  setModalMode(
                                    "view"
                                  );

                                  setOpenModal(
                                    true
                                  );
                                }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>

                            {/* EDIT */}
                            <Tooltip title="Edit">
                              <IconButton
                                color="secondary"
                                onClick={() => {
                                  setSelectedItem(
                                    item
                                  );

                                  setModalMode(
                                    "edit"
                                  );

                                  setOpenModal(
                                    true
                                  );
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>

                            {/* ENABLE / DISABLE */}
                            <Tooltip
                              title={
                                item?.is_active
                                  ? "Disable Event"
                                  : "Enable Event"
                              }
                            >
                              <span>
                                {statusLoadingId === item.id ? (
                                  <CircularProgress size={22} />
                                ) : (
                                  <Switch
                                    checked={item?.is_active}
                                    onChange={async () => {
                                      try {
                                        setStatusLoadingId(item.id);

                                        await updateSurveyEventStatus(
                                          item.id,
                                          !item?.is_active
                                        );

                                        queryClient.invalidateQueries(
                                          "autoNotificationCreatedEvent"
                                        );
                                      } catch (error) {
                                        console.error(
                                          "Status update failed",
                                          error
                                        );
                                      } finally {
                                        setStatusLoadingId(null);
                                      }
                                    }}

                                  />
                                )}
                              </span>
                            </Tooltip>

                            {/* REPORTS */}
                            <Tooltip title="View Reports">
                              <IconButton
                                color="info"
                                onClick={() => {
                                  navigate(`/surveys/notification-logs/${item.id}`);
                                }}
                              >
                                <Assessment />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                    >
                      <Box py={6}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          No records found
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={1}
                        >
                          No auto notification
                          events available
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          <Box
            display="flex"
            justifyContent="flex-end"
            borderTop="1px solid #eee"
          >
            <TablePagination
              component="div"
              count={totalCount}
              page={page - 1}
              onPageChange={
                handleChangePage
              }
              rowsPerPage={10}
              rowsPerPageOptions={[]}
            />
          </Box>

          <SurveySelectModal
            open={open}
            onClose={() => setOpen(false)}
            surveys={choicesListData?.list || []}
          />

        </Paper>
      )}

      {/* MODAL */}
      <QuestionEditAndViewModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        choiceId={Number(
          selectedItem?.choiceId
        )}
        mode={modalMode}
        initialData={selectedItem}
      />
    </Box>
  );
};

export default AutoNotificationCreatedEvent;